// Adapted from Jared Palmer's Radio Group (extended and removed Typescript…really need to add it back once I learn it)
// https://github.com/palmerhq/radio-group/blob/master/src/index.tsx
import * as React from 'react'
import classNames from 'classnames'
import { useField } from 'formik'

import { FormLabel, AlertText, HelperText } from 'library'
import styles from './styles.module.scss'

const codes = {
  RETURN: 13,
  SPACE: 32,
  END: 35,
  HOME: 36,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
}

const RadioGroupContext = React.createContext()

export function RadioGroup({
  label,
  labelDescription,
  helperText,
  name,
  children,
  value,
  isHorizontal,
  ...props
}) {
  const setChecked = React.useCallback(
    (v) => {
      if (props.onChange) {
        props.onChange(v)
      }
    },
    [props]
  )

  const otherRadioValues = React.Children.map(
    children,
    (child) => child.props.value
  )
  const ctx = React.useMemo(
    () => ({
      value,
      otherRadioValues,
      setChecked,
      name,
    }),
    [name, otherRadioValues, setChecked, value]
  )
  return (
    <RadioGroupContext.Provider value={ctx}>
      <div
        role="radiogroup"
        aria-labelledby={label || name}
        data-palmerhq-radio-group
        className={classNames(styles.radioGroup, {
          [styles.isHorizontal]: isHorizontal,
        })}
      >
        {/* Don't visually show if no explicit label is provided */}
        <FormLabel as="h3" srOnly={!label} className={styles.label}>
          {label || name}
        </FormLabel>
        {labelDescription && <HelperText>{labelDescription}</HelperText>}
        <div className={styles.radioButtonsWrapper}>
          {children}
          {helperText && <HelperText>{helperText}</HelperText>}
        </div>
      </div>
    </RadioGroupContext.Provider>
  )
}

export const Radio = React.forwardRef(
  ({ className, children, disabled, ...props }, passedRef) => {
    const [focus, setFocus] = React.useState(false)
    const ref = React.useRef(null)

    const ctx = React.useContext(RadioGroupContext)
    const { otherRadioValues, value, setChecked, name } = ctx
    const index = otherRadioValues.findIndex((i) => i === props.value)
    const count = otherRadioValues.length - 1
    React.useEffect(() => {
      if (value === props.value) {
        if (passedRef && passedRef.current !== null) {
          passedRef.current.focus()
        } else if (ref.current !== null) {
          ref.current.focus()
        }
      }
    }, [value, props.value, passedRef])

    const handleKeyDown = React.useCallback(
      (event) => {
        event.persist()
        let flag = false
        function setPrevious() {
          if (index === 0) {
            setChecked(otherRadioValues[count])
          } else {
            setChecked(otherRadioValues[index - 1])
          }
        }

        function setNext() {
          if (index === count) {
            setChecked(otherRadioValues[0])
          } else {
            setChecked(otherRadioValues[index + 1])
          }
        }

        switch (event.keyCode) {
          case codes.SPACE:
          case codes.RETURN:
            setChecked(props.value)
            flag = true
            break
          case codes.UP:
          case codes.LEFT:
            setPrevious()
            flag = true
            break
          case codes.DOWN:
          case codes.RIGHT:
            setNext()
            flag = true
            break
          default:
            break
        }

        if (flag) {
          event.stopPropagation()
          event.preventDefault()
        }
      },
      [index, setChecked, otherRadioValues, count, props.value]
    )

    const handleClick = React.useCallback(() => {
      setChecked(props.value)
    }, [props.value, setChecked])

    const handleBlur = React.useCallback(
      (e) => {
        if (props.onBlur) {
          props.onBlur(e)
        }
        setFocus(false)
      },
      [props]
    )

    const handleFocus = React.useCallback(
      (e) => {
        if (props.onFocus) {
          props.onFocus(e)
        }
        setFocus(true)
      },
      [props]
    )

    return (
      <div className={classNames(styles.radioWrapper, className)}>
        <label
          className={classNames(styles.radioLabel, {
            [styles.isDisabled]: disabled,
          })}
          htmlFor={`${name}-${props.value}`}
        >
          <input
            className={styles.radioInput}
            id={`${name}-${props.value}`}
            type="radio"
            value={value || props.value || ''}
            disabled={disabled}
            {...props}
            name={name}
            tabIndex={value === props.value || (!value && index === 0) ? 0 : -1}
            aria-checked={value === props.value}
            checked={value === props.value}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChange={handleClick}
            onKeyDown={handleKeyDown}
            data-palmerhq-radio
            data-palmerhq-radio-focus={focus}
            ref={(el) => {
              if (passedRef) {
                // eslint-disable-next-line no-param-reassign
                passedRef.current = el
              }
              ref.current = el
            }}
          />
          <div className={styles.circleWrapper}>
            <svg
              className={styles.outerCircle}
              focusable="false"
              viewBox="0 0 24 24"
              aria-hidden="true"
              role="presentation"
            >
              <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                <circle
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="#FFFFFF"
                  cx="12"
                  cy="12"
                  r="11"
                />
              </g>
            </svg>
            <svg
              className={styles.innerCircle}
              focusable="false"
              viewBox="0 0 24 24"
              aria-hidden="true"
              role="presentation"
            >
              <path d="M8.465 8.465C9.37 7.56 10.62 7 12 7C14.76 7 17 9.24 17 12C17 13.38 16.44 14.63 15.535 15.535C14.63 16.44 13.38 17 12 17C9.24 17 7 14.76 7 12C7 10.62 7.56 9.37 8.465 8.465Z" />
            </svg>
          </div>
          <span className={styles.textLabel}>{children}</span>
        </label>
      </div>
    )
  }
)

Radio.displayName = 'Radio'

export function FormRadioGroup({ label, ...props }) {
  const { name } = props
  const [{ onChange, onBlur, ...field }, meta] = useField(name)
  return (
    <div className={styles.radioGroupWrapper}>
      <RadioGroup
        {...props}
        {...field}
        label={label}
        name={name}
        onBlur={onBlur(name)}
        onChange={onChange(name)}
      />
      <AlertText
        isVisible={!!(meta.touched && meta.error)}
        variant="danger"
        id={`${label}-error`}
      >
        {meta.error}
      </AlertText>
    </div>
  )
}
